import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { jsPDF } from 'jspdf';
import { OnboardingData, SubjectPlan, WeekSchedule, DaySchedule, saveStudyPlan, getStudyPlan, toggleDayCompletion, StudyPlan } from '../services/onboardingService';
import { getExamById, ExamSubject } from '../services/examService';
import { getFilterOptions } from '../services/questionService';

// ==================== TIPOS ====================

interface StudyPlanDisplayProps {
  user: User | null;
  onboardingData: OnboardingData;
  onStartStudying: () => void;
  onBack: () => void;
  onViewQuestions: (subject: string, topic: string) => void;
  onViewFlashcards?: (subject: string, topic: string) => void;
  onResetPlan?: () => void; // Opcional para refazer plano
}

interface GeneratedPlan {
  subjects: SubjectPlan[];
  paretoTopics: string[];
  weeklySchedule: WeekSchedule[];
  tips: string[];
  totalWeeks: number;
  totalHours: number;
  hoursPerWeek: number;
  startDate?: string;
  onboardingSnapshot?: {
    targetDate?: string | null;
    hasNoDate: boolean;
    daysPerWeek: number;
    hoursPerDay: number;
    selectedExamId?: string | null;
    generatedAt?: string;
  };
}

interface TodayTask {
  dayName: string;
  subject: string;
  topic: string;
  hours: number;
  completed: boolean;
  weekNumber: number;
  dayIndex: number;
}

// ==================== DADOS E UTILITÁRIOS ====================

const defaultSubjects: SubjectPlan[] = [
  { name: 'Língua Portuguesa', priority: 'alta', hoursPerWeek: 4, topics: ['Interpretação', 'Gramática'] },
  { name: 'Raciocínio Lógico', priority: 'media', hoursPerWeek: 3, topics: ['Lógica Proposicional', 'Análise Combinatória'] },
  { name: 'Direito Constitucional', priority: 'alta', hoursPerWeek: 4, topics: ['Direitos Fundamentais', 'Organização do Estado'] },
  { name: 'Direito Administrativo', priority: 'media', hoursPerWeek: 3, topics: ['Atos Administrativos', 'Licitações'] },
  { name: 'Informática', priority: 'baixa', hoursPerWeek: 2, topics: ['Windows', 'Segurança da Informação'] },
];

const examSubjects: Record<string, SubjectPlan[]> = {
  'default': defaultSubjects,
  'TRT': [
    ...defaultSubjects,
    { name: 'Direito do Trabalho', priority: 'alta', hoursPerWeek: 5, topics: ['Contratos', 'Jornada'] },
    { name: 'Processo do Trabalho', priority: 'alta', hoursPerWeek: 5, topics: ['Recursos', 'Audiências'] },
  ],
  'TJ': [
    ...defaultSubjects,
    { name: 'Processo Civil', priority: 'alta', hoursPerWeek: 5, topics: ['Atos Processuais', 'Recursos'] },
    { name: 'Processo Penal', priority: 'media', hoursPerWeek: 4, topics: ['Inquérito', 'Prisão'] },
  ],
  'INSS': [
    ...defaultSubjects,
    { name: 'Direito Previdenciário', priority: 'alta', hoursPerWeek: 8, topics: ['Benefícios', 'Custeio'] },
  ],
  'Receita Federal': [
    ...defaultSubjects,
    { name: 'Direito Tributário', priority: 'alta', hoursPerWeek: 6, topics: ['Sistema Tributário', 'Impostos'] },
    { name: 'Contabilidade', priority: 'alta', hoursPerWeek: 6, topics: ['Balanço', 'Demonstrações'] },
  ],
  'Polícia Federal': [
    ...defaultSubjects,
    { name: 'Contabilidade', priority: 'alta', hoursPerWeek: 5, topics: ['Geral', 'Custos'] },
    { name: 'Informática Avançada', priority: 'alta', hoursPerWeek: 5, topics: ['Redes', 'Banco de Dados', 'Python'] },
  ],
  'Banco': [
    { name: 'Língua Portuguesa', priority: 'alta', hoursPerWeek: 4, topics: ['Interpretação', 'Gramática'] },
    { name: 'Matemática Financeira', priority: 'alta', hoursPerWeek: 4, topics: ['Juros', 'Amortização'] },
    { name: 'Conhecimentos Bancários', priority: 'alta', hoursPerWeek: 6, topics: ['Sistema Financeiro', 'Produtos'] },
    { name: 'Informática', priority: 'media', hoursPerWeek: 3, topics: ['Segurança', 'Office'] },
    { name: 'Vendas e Negociação', priority: 'media', hoursPerWeek: 3, topics: ['Técnicas de Vendas', 'Atendimento'] },
  ]
};

const motivationalMessages = [
  "Cada questão te aproxima da aprovação! 💪",
  "Você está construindo seu futuro! 🚀",
  "A consistência vence o talento! 🎯",
  "Hoje é dia de evoluir! 📈",
  "Sua dedicação vai valer a pena! ⭐",
  "Mais um dia de conquistas! 🏆"
];

const getRandomMotivation = () => motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];

const normalizeLabel = (text: string = "") =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

// ==================== COMPONENTE PRINCIPAL ====================

const StudyPlanDisplay: React.FC<StudyPlanDisplayProps> = ({
  user,
  onboardingData,
  onStartStudying,
  onBack,
  onViewQuestions,
  onViewFlashcards,
  onResetPlan
}) => {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);
  const [activeView, setActiveView] = useState<'hoje' | 'semana' | 'materias'>('hoje');
  const [motivation] = useState(getRandomMotivation());
  const [hardestSubject, setHardestSubject] = useState<string>("");

  // Calcula horas líquidas
  const weeklyHours = onboardingData.hoursPerDay * onboardingData.daysPerWeek;

  // Calcula semanas até a data-alvo
  const calculateWeeks = (): number => {
    if (onboardingData.hasNoDate) return 12;

    const parseDate = (raw: string): Date | null => {
      const value = String(raw || '').trim();
      if (!value) return null;

      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return Number.isNaN(dt.getTime()) ? null : dt;
      }

      const br = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (br) {
        const dt = new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
        return Number.isNaN(dt.getTime()) ? null : dt;
      }

      const dt = new Date(value);
      return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const targetDate = parseDate(onboardingData.targetDate);
    if (!targetDate) return 12;

    const today = new Date();
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.ceil(diffDays / 7);
    if (!Number.isFinite(weeks)) return 12;
    // Se a data vier no passado por erro de extração, não colapsar para 1 semana
    if (weeks < 1) return 12;
    return Math.max(1, Math.min(52, weeks));
  };

  const totalWeeks = calculateWeeks();
  const totalHours = weeklyHours * totalWeeks;

  // Encontra a tarefa de HOJE
  const getTodayTask = (): TodayTask | null => {
    if (!plan) return null;
    
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Domingo, 1 = Segunda...
    const daysMap = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const todayName = daysMap[dayOfWeek];
    
    // Calcula qual semana estamos baseado na data de início
    const startOfPlan = (() => {
      if (plan.startDate) return new Date(`${plan.startDate}T00:00:00`);
      const base = new Date();
      const day = base.getDay();
      base.setDate(base.getDate() - (day === 0 ? 6 : day - 1));
      return base;
    })();

    const diffDays = Math.floor((today.getTime() - startOfPlan.getTime()) / (1000 * 60 * 60 * 24));
    const currentWeek = Math.min(
      Math.max(1, Math.floor(diffDays / 7) + 1),
      plan.weeklySchedule.length
    );
    
    const weekSchedule = plan.weeklySchedule.find(w => w.weekNumber === currentWeek);
    if (!weekSchedule) return null;
    
    const dayIndex = weekSchedule.days.findIndex(d => d.dayName === todayName);
    const todaySchedule = weekSchedule.days[dayIndex];
    
    if (!todaySchedule) return null;
    
    return {
      ...todaySchedule,
      weekNumber: currentWeek,
      dayIndex
    };
  };

  // Calcula progresso geral
  const calculateProgress = () => {
    if (!plan) return { completed: 0, total: 0, percentage: 0, streak: 0 };
    
    let completed = 0;
    let total = 0;
    let currentStreak = 0;
    let streakBroken = false;
    
    // Conta dias completados (do mais recente para o mais antigo para calcular streak)
    for (const week of plan.weeklySchedule) {
      for (const day of week.days) {
        total++;
        if (day.completed) {
          completed++;
          if (!streakBroken) currentStreak++;
        } else {
          streakBroken = true;
        }
      }
    }
    
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
      streak: currentStreak
    };
  };

  const SUBJECT_ALIASES: Record<string, string> = {
    "língua portuguesa": "Português",
    "lingua portuguesa": "Português",
    "português": "Português",
    "portugues": "Português",
  };
  const toKey = (text: string = '') =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const canonicalizeSubject = (name: string = "") => {
    const key = name.toLowerCase().trim();
    return SUBJECT_ALIASES[key] || name;
  };

  const CARGO_SUBJECT_WHITELIST: Record<string, string[]> = {
    'agente administrativo': [
      'Português',
      'Língua Portuguesa',
      'Conhecimentos em Informática',
      'Noções de Informática',
      'Informática',
      'Matemática',
      'Raciocínio Lógico',
      'Matemática e Raciocínio Lógico',
      'Conhecimentos Gerais e Legislação Aplicada ao Município',
      'Legislação do Município',
      'Conhecimentos Gerais',
      'Atualidades',
    ],
  };

  const CARGO_TOPIC_WHITELIST: Record<string, Record<string, string[]>> = {
    'agente administrativo': {
      'Português': ['Interpretação de Texto', 'Ortografia', 'Acentuação', 'Morfologia', 'Sintaxe'],
      'Língua Portuguesa': ['Interpretação de Texto', 'Ortografia', 'Acentuação', 'Morfologia', 'Sintaxe'],
      'Noções de Informática': ['Conceitos Básicos de Informática', 'Hardware e Software', 'Windows', 'Linux', 'Internet e Segurança'],
      'Conhecimentos em Informática': ['Conceitos Básicos de Informática', 'Hardware e Software', 'Windows', 'Linux', 'Internet e Segurança'],
      'Informática': ['Conceitos Básicos de Informática', 'Hardware e Software', 'Windows', 'Linux', 'Internet e Segurança'],
      'Matemática': ['Operações Básicas', 'Razão e Proporção', 'Porcentagem', 'Regra de Três', 'Problemas Matemáticos'],
      'Raciocínio Lógico': ['Proposições', 'Conectivos Lógicos', 'Tabelas-Verdade', 'Sequências Lógicas', 'Problemas de Lógica'],
      'Matemática e Raciocínio Lógico': ['Porcentagem', 'Regra de Três', 'Proposições', 'Sequências Lógicas', 'Problemas de Lógica'],
      'Conhecimentos Gerais e Legislação Aplicada ao Município': ['Atualidades', 'História e Geografia Local', 'Lei Orgânica do Município', 'Noções de Administração Pública'],
      'Legislação do Município': ['Lei Orgânica do Município', 'Estrutura Administrativa Municipal', 'Serviço Público Municipal'],
      'Conhecimentos Gerais': ['Atualidades', 'História e Geografia Local', 'Cidadania e Serviço Público'],
      'Atualidades': ['Atualidades', 'Política', 'Economia', 'Sociedade', 'Cultura e fatos recentes'],
    },
  };
  const getCargoWhitelist = (cargo?: string) => {
    const key = toKey(String(cargo || ''));
    if (!key) return null;
    for (const [k, list] of Object.entries(CARGO_SUBJECT_WHITELIST)) {
      if (key.includes(k)) return list;
    }
    return null;
  };

  const getCargoTopicWhitelist = (cargo?: string) => {
    const key = toKey(String(cargo || ''));
    if (!key) return null;
    for (const [k, topics] of Object.entries(CARGO_TOPIC_WHITELIST)) {
      if (key.includes(k)) return topics;
    }
    return null;
  };

  const resolveCatalogSubject = (raw: string, catalogSubjects: string[]) => {
    const cleaned = canonicalizeSubject(String(raw || '').trim());
    const cleanedKey = toKey(cleaned);

    // Evita mapear "Conhecimentos Específicos" para "Conhecimentos Bancários" por similaridade fraca
    if (cleanedKey.includes('conhecimentos especific')) {
      const exactSpecific = (catalogSubjects || []).find((s) => toKey(s).includes('conhecimentos especific'));
      return exactSpecific || cleaned;
    }

    if (!cleaned) return '';

    const invalidPattern = /^(direito\s+[a-z]$|matematica\s+[a-z]$|informatica\s+[a-z]$)/i;
    if (invalidPattern.test(cleaned)) return '';

    if (!Array.isArray(catalogSubjects) || catalogSubjects.length === 0) return cleaned;

    const ck = toKey(cleaned);
    let exact = catalogSubjects.find((s) => toKey(s) === ck);
    if (exact) return exact;

    exact = catalogSubjects.find((s) => toKey(s).includes(ck) || ck.includes(toKey(s)));
    if (exact) return exact;

    const tokens = ck.split(' ').filter((t) => t.length > 2);
    if (tokens.length === 0) return cleaned;

    let best = '';
    let bestScore = 0;
    for (const s of catalogSubjects) {
      const sk = toKey(s);
      const score = tokens.filter((t) => sk.includes(t)).length;
      if (score > bestScore) {
        bestScore = score;
        best = s;
      }
    }

    return bestScore >= Math.max(1, Math.ceil(tokens.length / 2)) ? best : cleaned;
  };

  // Gera ou recupera o plano de estudos
  useEffect(() => {
    const loadOrGeneratePlan = async () => {
      if (!user) return;
      setLoading(true);

      try {
        const existingPlan = await getStudyPlan();

        const toIsoDate = (ts: any) => {
          if (!ts) return null;
          if (typeof ts === "string") return ts.split("T")[0];
          if (ts?.toDate) return ts.toDate().toISOString().split("T")[0];
          if (ts?.seconds) return new Date(ts.seconds * 1000).toISOString().split("T")[0];
          return null;
        };

        const getExtractedFingerprint = (items: any[] = []) => {
          return items
            .map((s) => `${String(s?.name || '').trim().toLowerCase()}|${Array.isArray(s?.topics) ? s.topics.length : 0}`)
            .filter(Boolean)
            .sort()
            .join('||');
        };

        const snapshotMatches = (plan: any) => {
          const snap = plan?.onboardingSnapshot;
          if (!snap) return false;

          const plannedDays = Array.isArray(plan?.weeklySchedule)
            ? plan.weeklySchedule.reduce((acc: number, w: any) => acc + (Array.isArray(w?.days) ? w.days.length : 0), 0)
            : 0;
          if (plannedDays <= 0) return false;
          if (snap.hasNoDate !== onboardingData.hasNoDate) return false;
          if (!onboardingData.hasNoDate && snap.targetDate !== onboardingData.targetDate) return false;
          if (snap.hoursPerDay !== onboardingData.hoursPerDay) return false;
          if (snap.daysPerWeek !== onboardingData.daysPerWeek) return false;
          if ((snap.selectedExamId || null) !== (onboardingData.selectedExamId || null)) return false;
          if ((snap.uploadId || null) !== ((onboardingData as any).uploadId || null)) return false;

          const currentExtracted = Array.isArray((onboardingData as any).extractedSubjects)
            ? (onboardingData as any).extractedSubjects
            : [];
          const currentExtractedCount = currentExtracted.length;
          const snapExtractedCount = Number(snap.extractedSubjectsCount || 0);
          if (currentExtractedCount !== snapExtractedCount) return false;

          const currentFingerprint = getExtractedFingerprint(currentExtracted);
          const snapFingerprint = String(snap.extractedSubjectsFingerprint || '');
          if (currentFingerprint !== snapFingerprint) return false;

          // se startDate ficou no passado, força regenerar
          if (plan.startDate) {
            const todayIso = new Date().toISOString().split("T")[0];
            if (plan.startDate < todayIso) return false;
          }

          // força regenerar se plano é muito antigo (>30d)
          const createdIso = toIsoDate(plan.createdAt) || toIsoDate(plan.updatedAt);
          if (createdIso) {
            const created = new Date(createdIso);
            const diffDays = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays > 30) return false;
          }

          return true;
        };

        if (existingPlan && existingPlan.targetExam === onboardingData.targetExam && snapshotMatches(existingPlan)) {
          setPlan(existingPlan);
          setLoading(false);
          return;
        }

        // Gera novo plano
        let realExamData = null;
        if (onboardingData.selectedExamId) {
          try {
            realExamData = await getExamById(onboardingData.selectedExamId);
          } catch (err) {
            console.error("Erro ao buscar edital:", err);
          }
        }

        let subjects: SubjectPlan[] = [];
        let planSource: 'extractedSubjects' | 'examId' | 'fallback' = 'fallback';
        let catalogSubjects: string[] = [];
        let catalogTopics: Record<string, string[]> = {};
        try {
          const opts = await Promise.race([
            getFilterOptions(),
            new Promise<null>((resolve) => setTimeout(() => resolve(null), 3500)),
          ]);
          if (opts) {
            catalogSubjects = Array.isArray((opts as any)?.subjects) ? (opts as any).subjects : [];
            catalogTopics = (((opts as any)?.topics) || {}) as Record<string, string[]>;
          }
        } catch (_) {}

        if (Array.isArray((onboardingData as any).extractedSubjects) && (onboardingData as any).extractedSubjects.length > 0) {
          const extracted = (onboardingData as any).extractedSubjects as { name: string; topics: string[]; confidence?: string }[];
          const cargoKey = (onboardingData as any).selectedCargo || '';
          const cargoWhitelist = getCargoWhitelist(cargoKey);
          const cargoTopicWhitelist = getCargoTopicWhitelist(cargoKey);
          const whitelistKeys = Array.isArray(cargoWhitelist) ? cargoWhitelist.map((x) => toKey(x)) : [];

          const mapped = extracted
            .map((s) => {
              const resolvedName = resolveCatalogSubject(s.name, catalogSubjects);
              if (!resolvedName) return null;

              if (whitelistKeys.length > 0) {
                const rk = toKey(resolvedName);
                const allowed = whitelistKeys.some((wk) => rk === wk || rk.includes(wk) || wk.includes(rk));
                if (!allowed) return null;
              }

              const cargoFixedTopics = Array.isArray(cargoTopicWhitelist?.[resolvedName]) ? cargoTopicWhitelist![resolvedName] : [];
              const baseTopics = cargoFixedTopics.length > 0
                ? cargoFixedTopics
                : (Array.isArray(catalogTopics?.[resolvedName]) ? catalogTopics[resolvedName] : []);
              const extractedTopics = Array.isArray(s.topics) ? s.topics.map((t) => String(t || '').trim()).filter(Boolean) : [];

              const validExtractedTopics = cargoFixedTopics.length > 0
                ? []
                : (baseTopics.length > 0
                  ? extractedTopics.filter((t) => baseTopics.some((bt) => toKey(bt) === toKey(t) || toKey(bt).includes(toKey(t)) || toKey(t).includes(toKey(bt))))
                  : extractedTopics);

              return {
                name: resolvedName,
                priority: s.confidence === 'high' ? 'alta' : (s.confidence === 'low' ? 'baixa' : 'media'),
                hoursPerWeek: s.confidence === 'high' ? 4 : 2,
                topics: validExtractedTopics.length > 0
                  ? validExtractedTopics.slice(0, 20)
                  : (baseTopics.length > 0 ? baseTopics.slice(0, 8) : ['Revisão geral']),
              } as SubjectPlan;
            })
            .filter((s): s is SubjectPlan => !!s);

          const dedup = new Map<string, SubjectPlan>();
          mapped.forEach((s) => {
            const k = toKey(s.name);
            if (!dedup.has(k)) dedup.set(k, s);
          });
          subjects = Array.from(dedup.values());
          if (subjects.length > 0) planSource = 'extractedSubjects';

          const cargoKeyFallback = (onboardingData as any).selectedCargo || '';
          const cargoWhitelistFallback = getCargoWhitelist(cargoKeyFallback);
          const cargoTopicWhitelistFallback = getCargoTopicWhitelist(cargoKeyFallback);
          if (subjects.length === 0 && Array.isArray(cargoWhitelistFallback) && cargoWhitelistFallback.length > 0) {
            const whitelistResolved = cargoWhitelistFallback
              .map((n) => resolveCatalogSubject(n, catalogSubjects) || n)
              .filter(Boolean);
            const unique = Array.from(new Set(whitelistResolved.map((s) => s.trim())));
            subjects = unique.map((name) => {
              const fixed = Array.isArray(cargoTopicWhitelistFallback?.[name]) ? cargoTopicWhitelistFallback![name] : [];
              const baseTopics = fixed.length > 0
                ? fixed
                : (Array.isArray(catalogTopics?.[name]) ? catalogTopics[name] : []);
              return {
                name,
                priority: 'media',
                hoursPerWeek: 2,
                topics: baseTopics.length > 0 ? baseTopics.slice(0, 8) : ['Revisão geral'],
              } as SubjectPlan;
            });
          }
        } else if (realExamData && realExamData.subjects.length > 0) {
          subjects = realExamData.subjects
            .map((s: ExamSubject) => {
              const resolvedName = resolveCatalogSubject(s.name, catalogSubjects);
              if (!resolvedName) return null;
              const baseTopics = Array.isArray(catalogTopics?.[resolvedName]) ? catalogTopics[resolvedName] : [];
              return {
                name: resolvedName,
                priority: (s.weight && s.weight >= 3) ? 'alta' : (s.weight === 1 ? 'baixa' : 'media'),
                hoursPerWeek: (s.weight && s.weight >= 3) ? 4 : 2,
                topics: Array.isArray(s.topics) && s.topics.length > 0
                  ? s.topics
                  : (baseTopics.length > 0 ? baseTopics.slice(0, 8) : ['Revisão geral'])
              } as SubjectPlan;
            })
            .filter((s): s is SubjectPlan => !!s);
          if (subjects.length > 0) planSource = 'examId';
        } else {
          await new Promise(resolve => setTimeout(resolve, 1500));
          subjects = JSON.parse(JSON.stringify(examSubjects['default'])).map((s: any) => ({
            ...s,
            name: canonicalizeSubject(s.name),
          }));

          const examLower = onboardingData.targetExam.toLowerCase();
          if (examLower.includes('trt') || examLower.includes('trabalho')) {
            subjects = JSON.parse(JSON.stringify(examSubjects['TRT'])).map((s: any) => ({ ...s, name: canonicalizeSubject(s.name) }));
          } else if (examLower.includes('tj') && !examLower.includes('trt')) {
            subjects = JSON.parse(JSON.stringify(examSubjects['TJ'])).map((s: any) => ({ ...s, name: canonicalizeSubject(s.name) }));
          } else if (examLower.includes('inss') || examLower.includes('previdência')) {
            subjects = JSON.parse(JSON.stringify(examSubjects['INSS'])).map((s: any) => ({ ...s, name: canonicalizeSubject(s.name) }));
          } else if (examLower.includes('receita') || examLower.includes('fiscal')) {
            subjects = JSON.parse(JSON.stringify(examSubjects['Receita Federal'])).map((s: any) => ({ ...s, name: canonicalizeSubject(s.name) }));
          } else if (examLower.includes('polícia') || examLower.includes('pf') || examLower.includes('prf')) {
            subjects = JSON.parse(JSON.stringify(examSubjects['Polícia Federal'])).map((s: any) => ({ ...s, name: canonicalizeSubject(s.name) }));
          } else if (
            examLower.includes('banco') ||
            examLower.includes('caixa economica') ||
            examLower.includes('caixa econômica') ||
            examLower.includes('banco do brasil') ||
            /\bbb\b/.test(examLower)
          ) {
            subjects = JSON.parse(JSON.stringify(examSubjects['Banco'])).map((s: any) => ({ ...s, name: canonicalizeSubject(s.name) }));
          }
        }

        // Hardening: evita plano vazio quando extração retorna sem matérias válidas
        if (!Array.isArray(subjects) || subjects.length === 0) {
          subjects = JSON.parse(JSON.stringify(examSubjects['default'])).map((s: any) => ({
            ...s,
            name: canonicalizeSubject(s.name),
          }));
        }

        // Garante estrutura mínima por matéria
        const examText = String(onboardingData?.targetExam || '').toLowerCase();
        const isBankExam = examText.includes('banco do brasil') || examText.includes('caixa econômica') || examText.includes('caixa economica') || /\bbb\b/.test(examText);

        subjects = subjects
          .map((s: any) => {
            const rawName = canonicalizeSubject(String(s?.name || '').trim() || 'Matéria principal');
            const normalizedName = toKey(rawName);
            const safeName = (!isBankExam && normalizedName.includes('conhecimentos bancarios'))
              ? 'Conhecimentos Específicos'
              : rawName;

            return {
              ...s,
              name: safeName,
              topics: Array.isArray(s?.topics) && s.topics.length > 0
                ? s.topics.filter(Boolean)
                : ['Revisão geral'],
              hoursPerWeek: Number(s?.hoursPerWeek || 0),
            };
          })
          .filter((s: any) => s.name);

        // Ajusta horas
        const totalSubjectHours = subjects.reduce((sum, s) => sum + (Number(s.hoursPerWeek) || 0), 0);
        const safeWeeklyHours = Math.max(1, Number(weeklyHours || 1));
        const adjustmentFactor = totalSubjectHours > 0 ? safeWeeklyHours / totalSubjectHours : 1;
        subjects = subjects.map(s => ({
          ...s,
          hoursPerWeek: Math.max(1, Math.round((Number(s.hoursPerWeek) || 1) * adjustmentFactor))
        }));

        // Pareto
        const paretoTopics = subjects
          .filter(s => s.priority === 'alta')
          .flatMap(s => s.topics.slice(0, 2))
          .slice(0, 6);

        // Cronograma começando HOJE e seguindo em frente
        const weeklySchedule: WeekSchedule[] = [];
        const daysOfWeek = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
        const todayIdx = new Date().getDay(); // 0=Domingo
        const rotationStart = todayIdx === 0 ? 6 : todayIdx - 1; // 0 -> Dom, queremos 6 (Domingo), 1 -> Segunda => 0
        const safeDaysPerWeek = Math.max(1, Number(onboardingData.daysPerWeek || 1));
        const safeHoursPerDay = Math.max(1, Number(onboardingData.hoursPerDay || 1));

        for (let week = 1; week <= totalWeeks; week++) {
          const days: DaySchedule[] = Array.from({ length: safeDaysPerWeek }, (_, d) => {
            const globalOffset = (week - 1) * safeDaysPerWeek + d;
            const dayName = daysOfWeek[(rotationStart + globalOffset) % 7];
            const subjectIndex = globalOffset % subjects.length;
            const subject = subjects[subjectIndex] || subjects[0];
            const safeTopics = Array.isArray(subject?.topics) && subject.topics.length > 0 ? subject.topics : ['Revisão geral'];
            const topicIndex = Math.floor(globalOffset / 2) % safeTopics.length;
            return {
              dayName,
              hours: safeHoursPerDay,
              subject: subject?.name || 'Matéria principal',
              topic: safeTopics[topicIndex] || safeTopics[0],
              completed: false
            };
          });

          weeklySchedule.push({
            weekNumber: week,
            title: `Semana ${week}`,
            days
          });
        }

        const startDate = new Date().toISOString().split("T")[0];

        const newPlan = {
          subjects,
          paretoTopics,
          weeklySchedule,
          tips: [],
          totalWeeks,
          totalHours,
          hoursPerWeek: weeklyHours,
          targetExam: onboardingData.targetExam,
          startDate,
          onboardingSnapshot: {
            targetDate: onboardingData.targetDate || null,
            hasNoDate: onboardingData.hasNoDate,
            daysPerWeek: onboardingData.daysPerWeek,
            hoursPerDay: onboardingData.hoursPerDay,
            selectedExamId: onboardingData.selectedExamId || null,
            uploadId: (onboardingData as any).uploadId || null,
            planSource,
            extractedSubjectsCount: Array.isArray((onboardingData as any).extractedSubjects)
              ? (onboardingData as any).extractedSubjects.length
              : 0,
            extractedSubjectsFingerprint: Array.isArray((onboardingData as any).extractedSubjects)
              ? (onboardingData as any).extractedSubjects
                  .map((s: any) => `${String(s?.name || '').trim().toLowerCase()}|${Array.isArray(s?.topics) ? s.topics.length : 0}`)
                  .filter(Boolean)
                  .sort()
                  .join('||')
              : '',
            generatedAt: new Date().toISOString(),
            startDate,
          },
        };

        await saveStudyPlan(newPlan);
        setPlan(newPlan);

      } catch (error) {
        console.error("Erro ao carregar plano:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrGeneratePlan();
  }, [onboardingData, weeklyHours, totalWeeks, totalHours, user]);

  const todayTask = getTodayTask();
  const progress = calculateProgress();

  const getWeeklyProgress = () => {
    if (!plan) return { completed: 0, total: 0, percentage: 0 };
    const week1 = plan.weeklySchedule?.[0];
    if (!week1) return { completed: 0, total: 0, percentage: 0 };
    const completed = week1.days.filter((d: any) => d.completed).length;
    const total = week1.days.length;
    return { completed, total, percentage: total ? Math.round((completed / total) * 100) : 0 };
  };

  const weeklyProgress = getWeeklyProgress();
  const weeklyRemaining = Math.max(0, weeklyProgress.total - weeklyProgress.completed);
  const weekRhythm = weeklyProgress.percentage >= 80 ? "Excelente" : weeklyProgress.percentage >= 50 ? "Bom" : "Atenção";

  const updateSubjectPriority = (name: string, priority: 'alta' | 'media' | 'baixa') => {
    if (!plan) return;
    const newPlan = { ...plan } as any;
    newPlan.subjects = newPlan.subjects.map((s: SubjectPlan) =>
      s.name === name ? { ...s, priority } : s
    );
    setPlan(newPlan);
  };

  const getSubjectCompletion = (name: string) => {
    if (!plan) return { completed: 0, total: 0 };
    let completed = 0;
    let total = 0;
    plan.weeklySchedule.forEach((week) => {
      week.days.forEach((day) => {
        if (normalizeLabel(day.subject) === normalizeLabel(name)) {
          total += 1;
          if ((day as any).completed) completed += 1;
        }
      });
    });
    return { completed, total, percentage: total ? Math.round((completed / total) * 100) : 0 };
  };

  const applyHardestSubject = () => {
    if (!plan || !hardestSubject) return;
    const newPlan = { ...plan } as any;
    newPlan.subjects = newPlan.subjects.map((s: SubjectPlan) =>
      s.name === hardestSubject ? { ...s, priority: 'alta' } : s
    );
    setPlan(newPlan);
  };

  const BRAND = {
    primary: [16, 185, 129] as [number, number, number],
    dark: [15, 23, 42] as [number, number, number],
    muted: [107, 114, 128] as [number, number, number],
    ink: [55, 65, 81] as [number, number, number],
  };

  const handleExportPDF = () => {
    if (!plan) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    const lineHeight = 6;
    let y = 18;

    const generatedAt = new Date().toLocaleString('pt-BR');
    const targetExam = onboardingData.targetExam || 'Não definido';
    const totalTasks = plan.weeklySchedule.reduce((acc, week) => acc + week.days.length, 0);

    const ensureSpace = (neededHeight: number) => {
      if (y + neededHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addWrappedText = (text: string, options?: { bold?: boolean; size?: number; color?: [number, number, number]; extraGap?: number }) => {
      const bold = options?.bold ?? false;
      const size = options?.size ?? 11;
      const color = options?.color ?? [17, 24, 39];
      const extraGap = options?.extraGap ?? 0;

      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(color[0], color[1], color[2]);

      const lines = doc.splitTextToSize(text, contentWidth);
      ensureSpace(lines.length * lineHeight);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + extraGap;
    };

    // CAPA
    doc.setFillColor(BRAND.primary[0], BRAND.primary[1], BRAND.primary[2]);
    doc.rect(0, 0, pageWidth, 55, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(255, 255, 255);
    doc.text('PLANO DE ESTUDOS', margin, 25);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Concurseiro Pobre', margin + 12, 34);
    doc.setFillColor(255, 255, 255);
    doc.circle(margin + 4, 32, 5, 'F');
    doc.setTextColor(BRAND.primary[0], BRAND.primary[1], BRAND.primary[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('CP', margin + 4, 33, { align: 'center' });
    doc.setTextColor(255, 255, 255);

    y = 72;
    addWrappedText(`Concurso: ${targetExam}`, { bold: true, size: 14, color: BRAND.dark, extraGap: 2 });
    addWrappedText(`Gerado em: ${generatedAt}`, { size: 10, color: BRAND.muted });
    addWrappedText(`Semanas planejadas: ${plan.totalWeeks} • Horas totais: ${plan.totalHours}h • Horas/semana: ${plan.hoursPerWeek}h`, { size: 10, color: BRAND.muted });
    addWrappedText(`Total de sessões planejadas: ${totalTasks}`, { size: 10, color: BRAND.muted, extraGap: 5 });

    addWrappedText('Resumo das semanas', { bold: true, size: 13, color: BRAND.primary, extraGap: 1 });
    plan.weeklySchedule.forEach((week) => {
      addWrappedText(`• Semana ${week.weekNumber}: ${week.title || 'Planejamento semanal'} (${week.days.length} sessão(ões))`, {
        size: 10,
        color: BRAND.ink,
        extraGap: 0.5,
      });
    });

    // Detalhamento em nova página
    doc.addPage();
    y = margin;

    addWrappedText('Detalhamento por semana', { bold: true, size: 16, color: BRAND.dark, extraGap: 2 });
    addWrappedText(`Concurso: ${targetExam}`, { size: 10, color: BRAND.muted, extraGap: 4 });

    const addSectionTitle = (text: string) => {
      addWrappedText(text, { bold: true, size: 12, color: BRAND.primary, extraGap: 1 });
    };

    plan.weeklySchedule.forEach((week) => {
      addSectionTitle(`Semana ${week.weekNumber} — ${week.title || ''}`.trim());
      week.days.forEach((day) => {
        const line = `• ${day.dayName}: ${day.subject} — ${day.topic} (${day.hours}h)`;
        addWrappedText(line, { size: 10, color: BRAND.ink, extraGap: 0.5 });
      });
      y += 2;
    });

    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text('Concurseiro Pobre • Plano de Estudos', margin, pageHeight - 8);
      doc.text(`Página ${page} de ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    }

    doc.save('plano-de-estudos-concurseiro-pobre.pdf');
  };

  const getVerticalizedEditalData = () => {
    if (!plan) return [] as Array<{ subject: string; topics: string[]; priority: string; hoursPerWeek: number | string }>;

    const subjectMap = new Map<string, Set<string>>();

    const addTopic = (subject: string, topic: string) => {
      const cleanSubject = String(subject || '').trim();
      const cleanTopic = String(topic || '').trim();
      if (!cleanSubject || !cleanTopic) return;
      if (!subjectMap.has(cleanSubject)) subjectMap.set(cleanSubject, new Set<string>());
      subjectMap.get(cleanSubject)!.add(cleanTopic);
    };

    plan.subjects.forEach((subject) => {
      (subject.topics || []).forEach((topic) => addTopic(subject.name, topic));
    });

    // Fallback defensivo: garante tópicos que só aparecem no cronograma semanal
    plan.weeklySchedule.forEach((week) => {
      week.days.forEach((day) => addTopic(day.subject, day.topic));
    });

    return Array.from(subjectMap.entries())
      .map(([subject, topics]) => {
        const meta = plan.subjects.find((s) => s.name === subject);
        return {
          subject,
          topics: Array.from(topics).sort((a, b) => a.localeCompare(b, 'pt-BR')),
          priority: meta?.priority || '-',
          hoursPerWeek: meta?.hoursPerWeek ?? '-',
        };
      })
      .sort((a, b) => a.subject.localeCompare(b.subject, 'pt-BR'));
  };

  const handleExportVerticalizedEdital = () => {
    if (!plan) return;

    const orderedSubjects = getVerticalizedEditalData();
    if (!orderedSubjects.length) return;

    const escapeCsv = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
    const rows: string[] = [
      ['Concurso', 'Matéria', 'Tópico', 'Prioridade', 'Horas por semana'].map(escapeCsv).join(','),
    ];

    orderedSubjects.forEach(({ subject, topics, priority, hoursPerWeek }) => {
      topics.forEach((topic) => {
        rows.push([
          onboardingData.targetExam || 'Não definido',
          subject,
          topic,
          String(priority),
          String(hoursPerWeek),
        ].map(escapeCsv).join(','));
      });
    });

    const csvContent = `\uFEFF${rows.join('\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'edital-verticalizado-concurseiro-pobre.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportVerticalizedEditalPDF = () => {
    if (!plan) return;

    const orderedSubjects = getVerticalizedEditalData();
    if (!orderedSubjects.length) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    let y = 16;

    const now = new Date();
    const generatedAt = now.toLocaleString('pt-BR');
    const concurso = onboardingData.targetExam || 'Não definido';
    const totalSubjects = orderedSubjects.length;
    const totalTopics = orderedSubjects.reduce((acc, item) => acc + item.topics.length, 0);

    const ensureSpace = (neededHeight: number) => {
      if (y + neededHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addWrapped = (text: string, opts?: { bold?: boolean; size?: number; color?: [number, number, number]; gap?: number }) => {
      const bold = opts?.bold ?? false;
      const size = opts?.size ?? 10;
      const color = opts?.color ?? [31, 41, 55];
      const gap = opts?.gap ?? 1;

      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(color[0], color[1], color[2]);
      const lines = doc.splitTextToSize(text, contentWidth);
      const lineHeight = size >= 12 ? 6 : 5;
      ensureSpace(lines.length * lineHeight + gap);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + gap;
    };

    // CAPA
    doc.setFillColor(BRAND.primary[0], BRAND.primary[1], BRAND.primary[2]);
    doc.rect(0, 0, pageWidth, 55, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(255, 255, 255);
    doc.text('EDITAL VERTICALIZADO', margin, 25);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.text('Concurseiro Pobre', margin + 12, 34);
    doc.setFillColor(255, 255, 255);
    doc.circle(margin + 4, 32, 5, 'F');
    doc.setTextColor(BRAND.primary[0], BRAND.primary[1], BRAND.primary[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('CP', margin + 4, 33, { align: 'center' });
    doc.setTextColor(255, 255, 255);

    doc.setTextColor(BRAND.ink[0], BRAND.ink[1], BRAND.ink[2]);
    y = 72;
    addWrapped(`Concurso: ${concurso}`, { bold: true, size: 14, color: BRAND.dark, gap: 3 });
    addWrapped(`Gerado em: ${generatedAt}`, { size: 11, color: BRAND.muted, gap: 2 });
    addWrapped(`Total de matérias: ${totalSubjects}`, { size: 11, color: BRAND.muted, gap: 1 });
    addWrapped(`Total de tópicos: ${totalTopics}`, { size: 11, color: BRAND.muted, gap: 6 });

    // Sumário
    addWrapped('Sumário por matéria', { bold: true, size: 13, color: BRAND.primary, gap: 2 });
    orderedSubjects.forEach(({ subject, topics }) => {
      addWrapped(`• ${subject}: ${topics.length} tópico(s)`, { size: 10, color: BRAND.ink, gap: 0.5 });
    });

    // Conteúdo detalhado
    doc.addPage();
    y = margin;
    addWrapped('Conteúdo detalhado', { bold: true, size: 16, color: BRAND.dark, gap: 3 });
    addWrapped(`Concurso: ${concurso}`, { size: 10, color: BRAND.muted, gap: 4 });

    orderedSubjects.forEach(({ subject, topics, priority, hoursPerWeek }) => {
      ensureSpace(10);
      addWrapped(`${subject}  •  Prioridade: ${priority}  •  ${hoursPerWeek}h/semana`, { bold: true, size: 12, color: BRAND.primary, gap: 1 });
      topics.forEach((topic) => {
        addWrapped(`• ${topic}`, { size: 10, color: BRAND.ink, gap: 0.4 });
      });
      y += 2;
    });

    // Rodapé com paginação
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);
      doc.text(`Concurseiro Pobre • Edital Verticalizado`, margin, pageHeight - 8);
      doc.text(`Página ${page} de ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    }

    doc.save('edital-verticalizado-concurseiro-pobre.pdf');
  };

  // ==================== LOADING ====================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30 animate-pulse">
            <span className="text-5xl">📚</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Preparando seu plano...</h2>
          <p className="text-gray-500">Estamos montando o melhor caminho para sua aprovação!</p>
          <div className="flex items-center justify-center gap-1 mt-6">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  // ==================== RENDER PRINCIPAL ====================

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-teal-50/30">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-emerald-100 px-4 py-3 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2">
          <button
            onClick={onBack}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full font-bold shadow-sm hover:bg-emerald-50 transition"
          >
            ← Voltar
          </button>
          <h1 className="font-black text-gray-900 tracking-tight">Meu Plano de Estudos</h1>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              onClick={handleExportVerticalizedEdital}
              className="hidden sm:inline-flex px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
              title="Exportar edital verticalizado (CSV)"
            >
              Exportar verticalizado (CSV)
            </button>
            <button
              onClick={handleExportVerticalizedEditalPDF}
              className="hidden sm:inline-flex px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
              title="Exportar edital verticalizado (PDF)"
            >
              Exportar verticalizado (PDF)
            </button>
            <button
              onClick={handleExportPDF}
              className="hidden sm:inline-flex px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition shadow-sm"
              title="Exportar PDF"
            >
              Exportar PDF
            </button>
            <button
              onClick={onResetPlan}
              className="px-3 py-2 text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-xl text-xs font-bold transition"
              title="Refazer Plano de Estudos"
            >
              Refazer plano
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-32">

        <div className="sm:hidden mb-4 grid grid-cols-1 gap-2">
          <button
            onClick={handleExportVerticalizedEdital}
            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-2xl text-sm font-bold"
          >
            Exportar verticalizado (CSV)
          </button>
          <button
            onClick={handleExportVerticalizedEditalPDF}
            className="w-full px-4 py-3 bg-violet-600 text-white rounded-2xl text-sm font-bold"
          >
            Exportar verticalizado (PDF)
          </button>
          <button
            onClick={handleExportPDF}
            className="w-full px-4 py-3 bg-emerald-500 text-white rounded-2xl text-sm font-bold"
          >
            Exportar PDF do plano
          </button>
        </div>

        {/* Próxima ação (fixa e clara) */}
        {todayTask && (
          <div className="bg-white rounded-3xl p-4 mb-4 border-2 border-emerald-200 shadow-sm">
            <p className="text-[11px] font-black uppercase tracking-[0.12em] text-emerald-700">Próxima ação</p>
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="font-bold text-gray-900">{todayTask.subject} • {todayTask.topic}</p>
                <p className="text-sm text-gray-600">{todayTask.hours}h hoje • Semana {todayTask.weekNumber}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => onViewQuestions(todayTask.subject, todayTask.topic)}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold"
                >
                  Iniciar estudo de hoje
                </button>
                <button
                  onClick={() => setActiveView('semana')}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold"
                >
                  Ver próximos dias
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Meta semanal (explicável) */}
        <div className="bg-white rounded-3xl p-4 mb-4 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-[0.12em]">Meta semanal</p>
              <p className="text-sm text-gray-700">Você concluiu {weeklyProgress.completed} de {weeklyProgress.total} tarefas • {plan?.hoursPerWeek || weeklyHours}h/semana</p>
              <p className="text-xs text-gray-500">Faltam {weeklyRemaining} tarefa(s) • Ritmo: <span className="font-semibold text-gray-700">{weekRhythm}</span></p>
            </div>
            <span className="text-sm font-semibold text-gray-800">{weeklyProgress.percentage}%</span>
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500" style={{ width: `${weeklyProgress.percentage}%` }} />
          </div>
        </div>

        {/* Card de Motivação + Streak */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-5 mb-6 text-white shadow-lg shadow-emerald-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔥</span>
              <div>
                <p className="text-emerald-100 text-xs font-medium">Sequência</p>
                <p className="text-xl font-black">{progress.streak} {progress.streak === 1 ? 'dia' : 'dias'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-emerald-100 text-xs font-medium">Progresso geral</p>
              <p className="text-xl font-black">{progress.percentage}%</p>
            </div>
          </div>
          <p className="text-emerald-50 text-sm">{motivation}</p>
        </div>

        {/* Navegação por Tabs */}
        <div className="mb-6">
          <div className="flex gap-2 bg-gray-100 p-1 rounded-2xl">
            {[
              { id: 'hoje', label: 'Meu dia', icon: '📍', helper: 'faça isso agora' },
              { id: 'semana', label: 'Próximos dias', icon: '📅', helper: 'o que vem depois' },
              { id: 'materias', label: 'Conteúdo', icon: '📚', helper: 'visão completa' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id as any)}
                className={`flex-1 py-3 px-2 sm:px-4 rounded-xl font-semibold text-xs sm:text-sm transition flex flex-col items-center justify-center gap-0.5 ${
                  activeView === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{tab.icon} {tab.label}</span>
                <span className="text-[10px] font-medium text-gray-400 hidden sm:block">{tab.helper}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ==================== VIEW: HOJE ==================== */}
        {activeView === 'hoje' && (
          <div className="space-y-6">
            {/* Card Principal - Tarefa de Hoje */}
            {todayTask ? (
              <div className={`rounded-3xl p-6 border-2 transition ${
                todayTask.completed 
                  ? 'bg-emerald-50 border-emerald-200' 
                  : 'bg-white border-gray-100 shadow-lg shadow-gray-100'
              }`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">
                      {todayTask.completed ? '✅ Concluído!' : '📍 Sua tarefa de hoje'}
                    </p>
                    <h2 className="text-2xl font-black text-gray-900">{todayTask.subject}</h2>
                    <p className="text-emerald-600 font-semibold">{todayTask.topic}</p>
                    <p className="text-xs text-gray-500">Tempo estimado: {todayTask.hours}h</p>
                  </div>
                  <div className="bg-gray-100 rounded-2xl px-4 py-2 text-center">
                    <p className="text-2xl font-black text-gray-900">{todayTask.hours}h</p>
                    <p className="text-xs text-gray-500">hoje</p>
                  </div>
                </div>

                {!todayTask.completed ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        onClick={() => onViewQuestions(
                          todayTask.subject,
                          todayTask.topic
                        )}
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-4 rounded-2xl transition shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2"
                      >
                        <span className="text-xl">🚀</span>
                        Estudar por Questões
                      </button>
                      {onViewFlashcards && (
                        <button
                          onClick={() => onViewFlashcards(
                            todayTask.subject,
                            todayTask.topic
                          )}
                          className="w-full bg-white border-2 border-emerald-100 text-emerald-700 font-bold py-4 rounded-2xl transition hover:bg-emerald-50 flex items-center justify-center gap-2"
                        >
                          <span className="text-xl">🃏</span>
                          Estudar por Flashcards
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={async () => {
                        const newPlan = { ...plan };
                        const week = newPlan.weeklySchedule.find(w => w.weekNumber === todayTask.weekNumber);
                        if (week && week.days[todayTask.dayIndex]) {
                          week.days[todayTask.dayIndex].completed = true;
                          setPlan(newPlan);
                          await toggleDayCompletion(todayTask.weekNumber, todayTask.dayIndex);
                        }
                      }}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-2xl transition flex items-center justify-center gap-2"
                    >
                      <span>✓</span>
                      Marcar como Concluído
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4 space-y-2">
                    <p className="text-emerald-600 font-semibold">Parabéns! Você completou o estudo de hoje! 🎉</p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <button
                        onClick={onStartStudying}
                        className="px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-2xl"
                      >
                        Praticar questões
                      </button>
                      <button
                        onClick={() => setActiveView('materias')}
                        className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl"
                      >
                        Revisar matérias
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 text-center border border-white/60 shadow-2xl">
                <span className="text-5xl mb-4 block">🎉</span>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Dia de descanso!</h2>
                <p className="text-gray-500">Aproveite para relaxar. Amanhã voltamos com tudo!</p>
              </div>
            )}

            {/* Dica Rápida */}
            <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
              <div className="flex gap-3">
                <span className="text-2xl">💡</span>
                <div>
                  <p className="font-semibold text-amber-800 text-sm">Dica do dia</p>
                  <p className="text-amber-700 text-sm">Foque em entender o "porquê" das respostas, não apenas decorar.</p>
                </div>
              </div>
            </div>

            {/* Próximos Dias */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span>📆</span> Próximos dias
              </h3>
              <div className="space-y-2">
                {plan.weeklySchedule[0]?.days.slice(0, 3).map((day, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-2xl ${
                      day.completed ? 'bg-emerald-50' : 'bg-white'
                    } border border-gray-100`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        day.completed ? 'bg-emerald-500 text-white' : 'bg-gray-100'
                      }`}>
                        {day.completed ? '✓' : day.dayName.slice(0, 3)}
                      </div>
                      <div>
                        <p className={`font-semibold ${day.completed ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {day.subject}
                        </p>
                        <p className="text-sm text-gray-500">{day.topic}</p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-400">{day.hours}h</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ==================== VIEW: SEMANA (V2) ==================== */}
        {activeView === 'semana' && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-2xl p-3 border border-gray-100">
                <p className="text-[11px] text-gray-500 uppercase font-bold tracking-[0.1em]">Concluídas</p>
                <p className="text-xl font-black text-gray-900">{weeklyProgress.completed}/{weeklyProgress.total}</p>
              </div>
              <div className="bg-white rounded-2xl p-3 border border-gray-100">
                <p className="text-[11px] text-gray-500 uppercase font-bold tracking-[0.1em]">Horas</p>
                <p className="text-xl font-black text-gray-900">{plan.hoursPerWeek - weeklyRemaining}h/{plan.hoursPerWeek}h</p>
              </div>
              <div className="bg-white rounded-2xl p-3 border border-gray-100">
                <p className="text-[11px] text-gray-500 uppercase font-bold tracking-[0.1em]">Risco</p>
                <p className={`text-xl font-black ${weeklyProgress.percentage >= 80 ? 'text-emerald-600' : weeklyProgress.percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{weekRhythm}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-100">
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-[0.1em] mb-2">Próxima melhor ação</p>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 truncate">{todayTask?.subject || plan.weeklySchedule[0]?.days?.[0]?.subject || 'Próxima tarefa'}</p>
                  <p className="text-sm text-gray-500 truncate">{todayTask?.topic || plan.weeklySchedule[0]?.days?.[0]?.topic || 'Revisão'} • {todayTask?.hours || plan.weeklySchedule[0]?.days?.[0]?.hours || 1}h</p>
                </div>
                <button
                  onClick={() => {
                    const d = todayTask || plan.weeklySchedule[0]?.days?.[0];
                    if (d) onViewQuestions(normalizeLabel(d.subject), normalizeLabel(d.topic));
                  }}
                  className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold"
                >
                  Começar
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {plan.weeklySchedule[0]?.days.map((day, index) => {
                const isLate = !day.completed && index < new Date().getDay() - 1;
                return (
                  <div key={index} className={`bg-white border rounded-xl p-3 ${day.completed ? 'border-emerald-200' : isLate ? 'border-red-200' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => {
                          const newPlan = { ...plan };
                          const week = newPlan.weeklySchedule[0];
                          if (week) {
                            week.days[index].completed = !week.days[index].completed;
                            setPlan(newPlan);
                            await toggleDayCompletion(1, index);
                          }
                        }}
                        className={`w-7 h-7 rounded-lg text-xs font-bold ${day.completed ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {day.completed ? '✓' : day.dayName.slice(0,2)}
                      </button>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${day.completed ? 'text-emerald-700 line-through' : 'text-gray-900'}`}>{day.subject}</p>
                        <p className="text-xs text-gray-500 truncate">{day.topic}</p>
                      </div>

                      <span className="text-xs text-gray-500 font-semibold">{day.hours}h</span>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${day.completed ? 'bg-emerald-100 text-emerald-700' : isLate ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {day.completed ? 'Concluído' : isLate ? 'Atrasado' : 'Pendente'}
                      </span>

                      <button
                        onClick={() => onViewQuestions(normalizeLabel(day.subject), normalizeLabel(day.topic))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-emerald-100 rounded-lg font-semibold text-gray-700"
                      >
                        Iniciar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ==================== VIEW: CONTEÚDO (V2) ==================== */}
        {activeView === 'materias' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-4 border border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900">Conteúdo do seu plano</h3>
                <p className="text-sm text-gray-500">{plan.subjects.length} matérias • {plan.hoursPerWeek}h por semana</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={hardestSubject}
                  onChange={(e) => setHardestSubject(e.target.value)}
                  className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800"
                >
                  <option value="">Matéria mais difícil...</option>
                  {plan.subjects.map((s) => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
                <button
                  onClick={applyHardestSubject}
                  className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl"
                >
                  Priorizar
                </button>
              </div>
            </div>

            {plan.subjects.map((subject, index) => {
              const completion = getSubjectCompletion(subject.name);
              return (
                <div key={index} className="bg-white rounded-2xl p-4 border border-gray-100">
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-gray-900">{subject.name}</h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          subject.priority === 'alta'
                            ? 'bg-red-100 text-red-700'
                            : subject.priority === 'media'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-600'
                        }`}>
                          {subject.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{subject.hoursPerWeek}h/semana • {completion.completed}/{completion.total} tarefas concluídas</p>
                    </div>
                    <select
                      value={subject.priority}
                      onChange={(e) => updateSubjectPriority(subject.name, e.target.value as any)}
                      className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-800"
                    >
                      <option value="alta">Alta</option>
                      <option value="media">Média</option>
                      <option value="baixa">Baixa</option>
                    </select>
                  </div>

                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                    <div className="h-full bg-emerald-500" style={{ width: `${completion.percentage || 0}%` }} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {subject.topics.map((topic, topicIndex) => (
                      <button
                        key={topicIndex}
                        onClick={() => onViewQuestions(normalizeLabel(subject.name), normalizeLabel(topic))}
                        className="text-left text-sm bg-gray-50 hover:bg-emerald-50 hover:text-emerald-700 text-gray-700 px-3 py-2 rounded-lg transition border border-gray-100"
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </main>

      {/* Bottom CTA Fixo */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-100 p-3">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            onClick={() => {
              if (todayTask) {
                onViewQuestions(todayTask.subject, todayTask.topic);
                return;
              }
              onStartStudying();
            }}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-3 rounded-2xl transition shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2"
          >
            <span>📝</span>
            Praticar questões de hoje
          </button>
          {onViewFlashcards && (
            <button
              onClick={() => {
                if (todayTask) onViewFlashcards(todayTask.subject, todayTask.topic);
              }}
              className="w-full bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 font-bold py-3 rounded-2xl transition"
            >
              Estudar por flashcards
            </button>
          )}
          <button
            onClick={() => setActiveView('materias')}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-2xl transition"
          >
            Ver conteúdo completo
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudyPlanDisplay;
